import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
} from '@angular/core';

/**
 * Permite scroll horizontal "arrastando" o conteúdo com o mouse pressionado
 * (drag-to-scroll), comportamento esperado no desktop para barras horizontais
 * como os chips de categoria.
 *
 * Além do arraste, a diretiva:
 * - converte a roda vertical do mouse em rolagem horizontal (mantendo o gesto
 *   horizontal nativo do trackpad intacto);
 * - expõe `podeRolarEsquerda` / `podeRolarDireita` para que o template mostre
 *   setas e esmaecimento apenas no lado em que existe conteúdo escondido;
 * - expõe `rolar()` para as setas de navegação.
 *
 * Uso:
 *   <div class="categorias-chips chips-scroll-viewport"
 *        appDragScroll #ds="appDragScroll"> ... </div>
 *
 *   <button [disabled]="!ds.podeRolarEsquerda" (click)="ds.rolar(-1)"> ... </button>
 *
 * O elemento que recebe a diretiva deve ser o container com `overflow-x: auto`.
 *
 * Suprime o clique disparado logo após um arraste (o `mouseup` ocorre antes do
 * `click`), evitando que arrastar a barra acione o item sob o cursor.
 */
@Directive({
  selector: '[appDragScroll]',
  exportAs: 'appDragScroll',
})
export class DragScrollDirective implements AfterViewInit, OnDestroy {
  private pressionado = false;
  private houveDrag = false;
  private inicioX = 0;
  private scrollLeftInicial = 0;

  /** Deslocamento mínimo (px) para considerar que houve arraste, não clique. */
  private readonly LIMIAR_DRAG_PX = 5;

  /**
   * Folga (px) para comparar posições de scroll. Larguras fracionárias fazem
   * `scrollLeft + clientWidth` nunca bater exatamente em `scrollWidth`, o que
   * deixaria a seta da direita acesa para sempre.
   */
  private readonly TOLERANCIA_PX = 2;

  /** Existe conteúdo escondido à esquerda (usado para seta e esmaecimento). */
  podeRolarEsquerda = false;

  /** Existe conteúdo escondido à direita (usado para seta e esmaecimento). */
  podeRolarDireita = false;

  private observadorTamanho?: ResizeObserver;
  private observadorConteudo?: MutationObserver;

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    // Captura o clique ANTES de chegar nos filhos para poder suprimi-lo após
    // um arraste. @HostListener('click') roda no bubbling (tarde demais).
    this.el.nativeElement.addEventListener('click', this.onClickCapture, true);

    // Adiado de propósito: avaliar aqui mudaria um binding já verificado nesta
    // passada de change detection (ExpressionChangedAfterItHasBeenCheckedError
    // em modo dev).
    setTimeout(() => this.atualizarEstado());

    // O container muda de largura ao redimensionar a janela ou ao empilhar as
    // colunas no breakpoint de tablet.
    this.observadorTamanho = new ResizeObserver(() => this.reavaliar());
    this.observadorTamanho.observe(this.el.nativeElement);

    // As categorias chegam por API depois da montagem: sem observar os filhos,
    // o estado ficaria congelado no "sem overflow" do primeiro render.
    this.observadorConteudo = new MutationObserver(() => this.reavaliar());
    this.observadorConteudo.observe(this.el.nativeElement, {
      childList: true,
      subtree: true,
    });
  }

  ngOnDestroy(): void {
    this.el.nativeElement.removeEventListener(
      'click',
      this.onClickCapture,
      true,
    );
    this.observadorTamanho?.disconnect();
    this.observadorConteudo?.disconnect();
  }

  /**
   * Rola aproximadamente uma "tela" de chips. `-1` volta, `1` avança.
   */
  rolar(direcao: -1 | 1): void {
    const elemento = this.el.nativeElement;
    // 80% da área visível mantém um chip de referência em tela; o piso de 120px
    // evita passos curtos demais em telas estreitas.
    const passo = Math.max(120, Math.round(elemento.clientWidth * 0.8));
    elemento.scrollBy({ left: direcao * passo, behavior: 'smooth' });
  }

  @HostListener('scroll')
  onScroll(): void {
    this.atualizarEstado();
  }

  /**
   * Roda vertical do mouse rola a faixa horizontalmente. O gesto horizontal do
   * trackpad (`deltaX`) continua sendo tratado pelo navegador.
   */
  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) {
      return;
    }

    const elemento = this.el.nativeElement;
    const antes = elemento.scrollLeft;
    elemento.scrollLeft = antes + event.deltaY;

    // Só consome o evento se a faixa realmente rolou; caso contrário a página
    // continua rolando normalmente ao chegar na ponta.
    if (elemento.scrollLeft !== antes) {
      event.preventDefault();
    }
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent): void {
    this.pressionado = true;
    this.houveDrag = false;
    this.inicioX = event.pageX - this.el.nativeElement.offsetLeft;
    this.scrollLeftInicial = this.el.nativeElement.scrollLeft;
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.pressionado) {
      return;
    }
    const x = event.pageX - this.el.nativeElement.offsetLeft;
    const deslocamento = x - this.inicioX;

    if (Math.abs(deslocamento) > this.LIMIAR_DRAG_PX) {
      this.houveDrag = true;
      this.el.nativeElement.scrollLeft = this.scrollLeftInicial - deslocamento;
      // Evita seleção de texto durante o arraste.
      event.preventDefault();
    }
  }

  @HostListener('mouseup')
  @HostListener('mouseleave')
  onMouseUp(): void {
    this.pressionado = false;
  }

  private readonly onClickCapture = (event: MouseEvent): void => {
    if (this.houveDrag) {
      event.preventDefault();
      event.stopPropagation();
      this.houveDrag = false;
    }
  };

  /**
   * Reavalia dentro da zona do Angular: ResizeObserver e MutationObserver podem
   * disparar fora dela, e aí a view não seria atualizada.
   */
  private reavaliar(): void {
    this.ngZone.run(() => this.atualizarEstado());
  }

  private atualizarEstado(): void {
    const elemento = this.el.nativeElement;
    const maximo = elemento.scrollWidth - elemento.clientWidth;

    const esquerda = elemento.scrollLeft > this.TOLERANCIA_PX;
    const direita = elemento.scrollLeft < maximo - this.TOLERANCIA_PX;

    if (esquerda === this.podeRolarEsquerda && direita === this.podeRolarDireita) {
      return;
    }

    this.podeRolarEsquerda = esquerda;
    this.podeRolarDireita = direita;
    this.cdr.markForCheck();
  }
}
